import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  resendEmailVerificationCodeAPI,
  verifyEmailCodeAPI,
} from "@/services/api/user/signup";
import Modal from "@/components/Modal";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import { buildSignupQuery, readSignupParams } from "./signupState";

const EMAIL_STATUS = {
  CODE_EXPIRED: -20019,
  CODE_INVALID: -20020,
  TOO_MANY_ATTEMPTS: -20021,
  CODE_ALREADY_USED: -20022,
};

/**
 * Signup step 2 — email verification. Ports the RN `EmailVerificationScreen`:
 * 60s resend countdown and a server-side code check (the code is no longer
 * echoed to the web client).
 */
const EmailVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readSignupParams(search);

  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [seconds, setSeconds] = useState(60);
  const [busy, setBusy] = useState(false);
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  // The verification code is checked server-side, so it never travels on the
  // URL; strip any legacy `code` a bookmark/back-navigation may still carry.
  const nextParams = { ...params };
  delete nextParams.code;

  useEffect(() => {
    const myInterval = setInterval(() => {
      setSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(myInterval);
  }, []);

  const showAlert = (text: string) => {
    setModalAlertText(t(text));
    setModalAlertVisible(true);
  };

  const errorText = (resp: { result?: unknown } | undefined, fallback: string) =>
    typeof resp?.result === "string" && resp.result ? resp.result : fallback;

  const onPressEnter = async () => {
    if (!params.email) {
      return showAlert("Please fill in all the blank");
    }
    if (!emailVerificationCode.trim()) {
      return showAlert("Enter the verification code");
    }
    setBusy(true);
    const resp = await verifyEmailCodeAPI(params.email, emailVerificationCode.trim());
    setBusy(false);
    if (resp?.status === 0 && resp?.result?.verified) {
      navigate(`/signup/profile${buildSignupQuery(nextParams)}`);
      return;
    }
    if (resp?.status === EMAIL_STATUS.CODE_EXPIRED) {
      return showAlert("Verification code expired, please resend");
    }
    if (resp?.status === EMAIL_STATUS.CODE_INVALID) {
      return showAlert("Invalid verification code");
    }
    if (resp?.status === EMAIL_STATUS.TOO_MANY_ATTEMPTS) {
      return showAlert("Too many attempts, please resend");
    }
    if (resp?.status === EMAIL_STATUS.CODE_ALREADY_USED) {
      return showAlert("Verification code already used, please resend");
    }
    showAlert(errorText(resp, "Network error, please try again"));
  };

  const onPressResendVerificationCode = async () => {
    // This page renders on whatever URL it is opened with, so a direct load /
    // bookmark / refresh of a partial query (`/signup/verify` without `email`)
    // leaves the resend without a recipient. Bail out before any request or
    // navigation instead of firing a call the backend can only reject.
    if (!params.email || !params.username) {
      return showAlert("Please fill in all the blank");
    }
    setBusy(true);
    const resp = await resendEmailVerificationCodeAPI(params.email, params.username);
    setBusy(false);
    if (resp?.status === 0) {
      setSeconds(60);
      return;
    }
    if (resp?.status === -20000) {
      return showAlert("Email Existed");
    }
    if (resp?.status === -20001) {
      return showAlert("Username Existed");
    }
    showAlert(errorText(resp, "Please fill in all the blank"));
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">{t("email")}</h1>
      <p className="mt-1 text-sm text-gray-500">
        {t("Enter the verification code")}
        <br />
        <span className="font-semibold text-[#333]">{params.email}</span>
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("Verification Code")}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={emailVerificationCode}
            onChange={(e) => setEmailVerificationCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onPressEnter()}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-center text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#ffa217]"
            placeholder="000000"
          />
        </div>

        {seconds === 0 ? (
          <button
            type="button"
            onClick={onPressResendVerificationCode}
            disabled={busy}
            className="w-full rounded-lg border border-red-400 py-2.5 text-sm font-bold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
          >
            {t("RESEND VERIFICATION CODE")}
          </button>
        ) : (
          <div className="py-2.5 text-center text-sm text-gray-500">
            {seconds} {t("s")}
          </div>
        )}

        <button
          type="button"
          onClick={onPressEnter}
          disabled={busy}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-50"
        >
          {t("ENTER")}
        </button>

        <button
          type="button"
          onClick={() => navigate(`/signup${buildSignupQuery(nextParams)}`)}
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EmailVerificationPage;
